import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-card)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            Privacy Policy
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            Last Updated: January 15, 2026
          </p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          
          {/* Introduction */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Shield size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Introduction
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              LG Law Office ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              By using our website or services, you consent to the data practices described in this policy.
            </p>
          </div>

          {/* Information We Collect */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Eye size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Information We Collect
              </h2>
            </div>
            
            <h3 className="heading-5" style={{ color: 'var(--text-white)', marginBottom: '16px', marginTop: '24px' }}>
              Personal Information
            </h3>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px', marginBottom: '24px' }}>
              <li>Fill out a contact form or request a consultation</li>
              <li>Call or email our office</li>
              <li>Subscribe to our newsletter or updates</li>
              <li>Provide information during a legal consultation</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
              This information may include: name, email address, phone number, mailing address, case details, and any other information you choose to provide.
            </p>

            <h3 className="heading-5" style={{ color: 'var(--text-white)', marginBottom: '16px', marginTop: '24px' }}>
              Automatically Collected Information
            </h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
              When you visit our website, we may automatically collect certain information about your device, including:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
            </ul>
          </div>

          {/* How We Use Your Information */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <FileText size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                How We Use Your Information
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We use the information we collect to:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li>Respond to your inquiries and provide legal services</li>
              <li>Evaluate potential cases and provide consultations</li>
              <li>Communicate with you about your case or consultation</li>
              <li>Send you updates, newsletters, or marketing communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
              <li>Detect, prevent, and address technical issues or fraud</li>
            </ul>
          </div>

          {/* Information Security */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Lock size={32} color="var(--brand-primary)" />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>
                Information Security
              </h2>
            </div>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </div>

          {/* Disclosure of Information */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Disclosure of Information
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We may share your information in the following situations:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px' }}>
              <li><strong>With Your Consent:</strong> We may disclose your information with your explicit consent</li>
              <li><strong>Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal processes</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of assets</li>
            </ul>
          </div>

          {/* Your Rights */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Your Privacy Rights
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              Depending on your location, you may have the following rights:
            </p>
            <ul style={{ color: 'var(--text-white)', lineHeight: '2', paddingLeft: '24px', marginBottom: '24px' }}>
              <li>Access to the personal information we hold about you</li>
              <li>Correction of inaccurate or incomplete information</li>
              <li>Deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Restriction of processing of your information</li>
            </ul>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              To exercise these rights, please contact us at 1(909)397-7260.
            </p>
          </div>

          {/* Cookies */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Cookies and Tracking Technologies
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </div>

          {/* Children's Privacy */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Children's Privacy
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8' }}>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you become aware that a child has provided us with personal information, please contact us.
            </p>
          </div>

          {/* Changes to Policy */}
          <div style={{ marginBottom: '48px' }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Changes to This Privacy Policy
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>

          {/* Contact Us */}
          <div style={{
            background: 'var(--bg-card)',
            padding: '32px',
            borderRadius: '12px',
            border: '2px solid var(--brand-primary)'
          }}>
            <h2 className="heading-3" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
              Contact Us
            </h2>
            <p style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '16px' }}>
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div style={{ color: 'var(--text-white)', lineHeight: '2' }}>
              <p><strong>LG Law Office</strong></p>
              <p>P.O. Box 2769, Pomona, CA 91769</p>
              <p>Phone: 1(909)397-7260</p>
            </div>
          </div>

          {/* Back to Home */}
          <div style={{ marginTop: '48px', textAlign: 'center' }}>
            <Link to="/" className="btn-primary">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;